import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Eye,
  MousePointerClick,
  AlertTriangle,
  Ban,
  ShieldAlert,
  ExternalLink,
} from "lucide-react";
import { formatDatePretty } from "@/lib/dateUtils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface EmailEvent {
  id: number;
  sendId?: number;
  type: string;
  occurredAt: string;
  userAgent?: string;
  ipAddress?: string;
  location?: string;
  clickUrl?: string;
  bounceReason?: string;
}

type EmailEventWithRecipient = EmailEvent & {
  recipientEmail: string;
  recipientId: number;
};

interface Recipient {
  id: number;
  userId?: number;
  email: string;
  username?: string;
  status: string;
  error?: string;
  sentAt: string;
   suppressionReason?: string;
  events?: EmailEvent[];
}

interface EventStats {
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  spam: number;
  unsubscribe: number;
}

interface CampaignRunDetails {
  runId: number;
  campaignId: number;
  campaignName: string;
  runType: string;
  status: string;
  recipientCount: number;
  scheduledAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  templateName?: string | null;
  criteriaName?: string | null;
  description?: string | null;
}

interface JourneyDetails {
  journeyId: number;
  journeyName: string;
  description?: string | null;
}

interface EmailReportDetailViewProps {
  type: "campaign" | "journey";
  campaignRun?: CampaignRunDetails | null;
  journey?: JourneyDetails | null;
  eventStats?: EventStats | null;
}

const getStatusBadge = (status: string) => {
  const normalized = status.toLowerCase();

  switch (status) {
    case "completed":
      return (
        <Badge className="bg-emerald-100 text-emerald-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </Badge>
      );
    case "failed":
      return (
        <Badge variant="destructive">
          <XCircle className="w-3 h-3 mr-1" />
          Failed
        </Badge>
      );
    case "scheduled":
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <Clock className="w-3 h-3 mr-1" />
          Scheduled
        </Badge>
      );
    case "sending":
    case "in_progress":
      return (
        <Badge variant="secondary">
          <Clock className="w-3 h-3 mr-1" />
          In Progress
        </Badge>
      );
    default:
      if (normalized === "delivered") {
        return <Badge className="bg-emerald-500 text-white">delivered</Badge>;
      }
      if (normalized === "bounced") {
        return <Badge className="bg-red-500 text-white">bounced</Badge>;
      }
      if (normalized === "spam" || normalized === "complained") {
        return <Badge className="bg-orange-500 text-white">spam</Badge>;
      }
      return <Badge>{status}</Badge>;
  }
};

const getEventTypeBadge = (type: string) => {
  switch (type.toLowerCase()) {
    case "delivered":
      return (
        <Badge className="bg-emerald-100 text-emerald-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Delivered
        </Badge>
      );
    case "open":
      return (
        <Badge className="bg-blue-100 text-blue-800">
          <Eye className="w-3 h-3 mr-1" />
          Opened
        </Badge>
      );
    case "click":
      return (
        <Badge className="bg-purple-100 text-purple-800">
          <MousePointerClick className="w-3 h-3 mr-1" />
          Clicked
        </Badge>
      );
    case "bounce":
      return (
        <Badge className="bg-red-100 text-red-800">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Bounced
        </Badge>
      );
    case "spam":
      return (
        <Badge className="bg-orange-100 text-orange-800">
          <ShieldAlert className="w-3 h-3 mr-1" />
          Spam
        </Badge>
      );
    case "unsubscribe":
      return (
        <Badge className="bg-gray-100 text-gray-800">
          <Ban className="w-3 h-3 mr-1" />
          Unsubscribed
        </Badge>
      );
    default:
      return <Badge>{type}</Badge>;
  }
};

export default function EmailReportDetailView({
  type,
  campaignRun,
  journey,
  eventStats: providedEventStats,
}: EmailReportDetailViewProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("recipients");
  const [activeEventType, setActiveEventType] = useState<
    "delivered" | "open" | "click" | "bounce" | "spam" | "unsubscribe"
  >("delivered");
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const [timelinePage, setTimelinePage] = useState(1);
  const [selectedSendIds, setSelectedSendIds] = useState<number[]>([]);

  const RECIPIENTS_PAGE_SIZE = 50;
  const EVENTS_PAGE_SIZE = 50;
  const TIMELINE_PAGE_SIZE = 50;

  const campaignId = campaignRun?.campaignId;
  const runId = campaignRun?.runId;
  const journeyId = journey?.journeyId;

  // Fetch recipients for campaign runs
  const { data: campaignData, isLoading: campaignLoading } = useQuery({
    queryKey: [
      "/api/admin/crm/campaigns",
      campaignId,
      "recipients",
      runId,
      recipientsPage,
    ],
    enabled: type === "campaign" && !!campaignId && !!runId,
    queryFn: async () => {
      if (!campaignId || !runId) {
        return { recipients: [], eventStats: null, pagination: null };
      }
      const response = await apiRequest(
        "GET",
        `/api/admin/crm/campaigns/${campaignId}/recipients?runId=${runId}&page=${recipientsPage}&limit=${RECIPIENTS_PAGE_SIZE}`
      );
      return response.json();
    },
  });

  // Fetch recipients for journeys
  const { data: journeyData, isLoading: journeyLoading } = useQuery({
    queryKey: [
      "/api/admin/crm/email-reports/journey",
      journeyId,
      recipientsPage,
    ],
    enabled: type === "journey" && !!journeyId,
    queryFn: async () => {
      if (!journeyId) {
        return { recipients: [], eventStats: null, pagination: null };
      }
      const response = await apiRequest(
        "GET",
        `/api/admin/crm/email-reports/journey/${journeyId}?page=${recipientsPage}&limit=${RECIPIENTS_PAGE_SIZE}`
      );
      return response.json();
    },
  });

  const recipients: Recipient[] =
    type === "campaign"
      ? campaignData?.recipients || []
      : journeyData?.recipients || [];

  const eventsFromApi: EmailEventWithRecipient[] =
    type === "campaign"
      ? campaignData?.events || []
      : journeyData?.events || [];

  const pagination =
    type === "campaign" ? campaignData?.pagination : journeyData?.pagination;

  const eventStats: EventStats | null =
    providedEventStats ||
    (type === "campaign"
      ? campaignData?.eventStats
      : journeyData?.eventStats) ||
    null;

  const isLoading = type === "campaign" ? campaignLoading : journeyLoading;

  // Collect all events for Events/Timeline tabs
  const allEvents: EmailEventWithRecipient[] = useMemo(() => {
    if (eventsFromApi && eventsFromApi.length > 0) {
      return [...eventsFromApi].sort(
        (a, b) =>
          new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
      );
    }

    // Fallback: derive from recipients if backend doesn't provide events yet
    const events: EmailEventWithRecipient[] = [];
    recipients.forEach((recipient) => {
      if (recipient.events && recipient.events.length > 0) {
        recipient.events.forEach((event) => {
          events.push({
            ...event,
            recipientEmail: recipient.email,
            recipientId: recipient.id,
          });
        });
      }
    });

    return events.sort(
      (a, b) =>
        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
    );
  }, [eventsFromApi, recipients]);

  const stats = useMemo(
    () => ({
      delivered: recipients.filter((recipient) =>
        ["sent", "completed", "delivered"].includes(
          recipient.status.toLowerCase()
        )
      ).length,
      failed: recipients.filter((recipient) =>
        ["failed", "bounced"].includes(recipient.status.toLowerCase())
      ).length,
    }),
    [recipients]
  );

  // Paginated views
  const totalRecipients = pagination?.total ?? recipients.length;
  const recipientsLimit = pagination?.limit ?? RECIPIENTS_PAGE_SIZE;
  const totalRecipientPages = Math.max(
    1,
    Math.ceil(totalRecipients / recipientsLimit)
  );
  const currentRecipientsPage = pagination?.page ?? recipientsPage;

  // Within each page, show most recently sent emails first.
  // Entries with no sentAt (displayed as "Pending") are pushed to the bottom
  // so they don't clutter recent activity.
  const paginatedRecipients = useMemo(() => {
    const pageRecipients = [...recipients];
    pageRecipients.sort((a, b) => {
      const aSent = a.sentAt ? new Date(a.sentAt).getTime() : 0;
      const bSent = b.sentAt ? new Date(b.sentAt).getTime() : 0;

      if (!aSent && !bSent) return 0;
      if (!aSent) return 1; // a pending => after b
      if (!bSent) return -1; // b pending => after a

      return bSent - aSent; // newest first
    });
    return pageRecipients;
  }, [recipients]);

  const toggleRecipientSelection = (sendId: number) => {
    setSelectedSendIds((prev) =>
      prev.includes(sendId)
        ? prev.filter((id) => id !== sendId)
        : [...prev, sendId]
    );
  };

  const toggleSelectAllFailedOnPage = () => {
    const failedIds = paginatedRecipients
      .filter((recipient) => recipient.status.toLowerCase() === "failed")
      .map((recipient) => recipient.id);

    const allSelected =
      failedIds.length > 0 &&
      failedIds.every((id) => selectedSendIds.includes(id));

    setSelectedSendIds(allSelected ? [] : failedIds);
  };

  const retryJourneyEmailsMutation = useMutation({
    mutationFn: async (sendIds: number[]) => {
      if (!journeyId || sendIds.length === 0) return;
      const response = await apiRequest(
        "POST",
        `/api/admin/crm/email-reports/journey/${journeyId}/retry`,
        { sendIds }
      );
      return response.json();
    },
    onSuccess: (data) => {
      setSelectedSendIds([]);
      if (journeyId) {
        queryClient.invalidateQueries({
          queryKey: ["/api/admin/crm/email-reports/journey", journeyId],
        });
      }
      toast({
        title: "Retry triggered",
        description:
          data?.processed != null
            ? `Processed ${data.processed} email(s), ${
                data.failed || 0
              } failed.`
            : "Selected emails have been requeued for sending.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Retry failed",
        description:
          error?.message || "Could not retry selected journey emails.",
        variant: "destructive",
      });
    },
  });

  const clearFailedJourneyEmailsMutation = useMutation({
    mutationFn: async () => {
      if (!journeyId) return;
      const response = await apiRequest(
        "POST",
        `/api/admin/crm/email-reports/journey/${journeyId}/clear-failed`,
        { hours: 24 }
      );
      return response.json();
    },
    onSuccess: (data) => {
      if (journeyId) {
        queryClient.invalidateQueries({
          queryKey: ["/api/admin/crm/email-reports/journey", journeyId],
        });
      }
      toast({
        title: "Cleared failed emails",
        description:
          data?.deleted != null
            ? `Removed ${data.deleted} failed send(s) from the last 24 hours.`
            : "Failed emails have been cleared.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Clear failed emails failed",
        description: error?.message || "Could not clear failed journey emails.",
        variant: "destructive",
      });
    },
  });

  const filteredEventsByType = useMemo(
    () => allEvents.filter((e) => e.type.toLowerCase() === activeEventType),
    [allEvents, activeEventType]
  );

  const totalEventPages = Math.max(
    1,
    Math.ceil(filteredEventsByType.length / EVENTS_PAGE_SIZE)
  );
  const currentEventsPage = Math.min(eventsPage, totalEventPages);
  const paginatedEvents = useMemo(
    () =>
      filteredEventsByType.slice(
        (currentEventsPage - 1) * EVENTS_PAGE_SIZE,
        currentEventsPage * EVENTS_PAGE_SIZE
      ),
    [filteredEventsByType, currentEventsPage]
  );

  const totalTimelinePages = Math.max(
    1,
    Math.ceil(allEvents.length / TIMELINE_PAGE_SIZE)
  );
  const currentTimelinePage = Math.min(timelinePage, totalTimelinePages);
  const paginatedTimelineEvents = useMemo(
    () =>
      allEvents.slice(
        (currentTimelinePage - 1) * TIMELINE_PAGE_SIZE,
        currentTimelinePage * TIMELINE_PAGE_SIZE
      ),
    [allEvents, currentTimelinePage]
  );

  const displayName =
    type === "campaign" ? campaignRun?.campaignName : journey?.journeyName;
  const displayDescription =
    type === "campaign" ? campaignRun?.description : journey?.description;

  if (!displayName) return null;

  return (
    <div className="space-y-6">
      {/* Run/Journey Info Cards */}
      {type === "campaign" && campaignRun && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
          <div>
            <p className="text-sm text-muted-foreground">Run ID</p>
            <p className="font-semibold">{campaignRun.runId}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Type</p>
            <p className="font-semibold">{campaignRun.runType}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Recipients</p>
            <p className="font-semibold">{campaignRun.recipientCount}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Status</p>
            <div>{getStatusBadge(campaignRun.status)}</div>
          </div>
        </div>
      )}

      {/* Event Statistics Cards */}
      {eventStats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Delivered
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {eventStats.delivered}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Opened
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {eventStats.opened}
              </div>
              {eventStats.delivered > 0 && (
                <p className="text-xs text-muted-foreground">
                  {Math.round((eventStats.opened / eventStats.delivered) * 100)}
                  % rate
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Clicked
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {eventStats.clicked}
              </div>
              {eventStats.opened > 0 && (
                <p className="text-xs text-muted-foreground">
                  {Math.round((eventStats.clicked / eventStats.opened) * 100)}%
                  CTR
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Bounced
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {eventStats.bounced}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Spam
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                {eventStats.spam}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Unsubscribed
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-600">
                {eventStats.unsubscribe}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Timestamps (for campaigns only) */}
      {type === "campaign" && campaignRun && (
        <div className="grid grid-cols-2 gap-4">
          {campaignRun.scheduledAt && (
            <div>
              <p className="text-sm text-muted-foreground">Scheduled</p>
              <p className="text-sm">
                {formatDatePretty(campaignRun.scheduledAt)}
              </p>
            </div>
          )}
          {campaignRun.startedAt && (
            <div>
              <p className="text-sm text-muted-foreground">Started</p>
              <p className="text-sm">
                {formatDatePretty(campaignRun.startedAt)}
              </p>
            </div>
          )}
          {campaignRun.completedAt && (
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-sm">
                {formatDatePretty(campaignRun.completedAt)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Template and Criteria (for campaigns only) */}
      {type === "campaign" && campaignRun && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Template</p>
            <p className="font-semibold">{campaignRun.templateName || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Criteria</p>
            <p className="font-semibold">
              {campaignRun.criteriaName || "Manual"}
            </p>
          </div>
        </div>
      )}

      {displayDescription && (
        <div>
          <p className="text-sm text-muted-foreground">Description</p>
          <p className="text-sm">{displayDescription}</p>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="recipients">
            Recipients ({recipients.length})
          </TabsTrigger>
          <TabsTrigger value="events">Events ({allEvents.length})</TabsTrigger>
          <TabsTrigger value="timeline">
            Timeline ({allEvents.length})
          </TabsTrigger>
        </TabsList>

        {/* Recipients Tab */}
        <TabsContent value="recipients" className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading recipients...
            </div>
          ) : recipients.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No recipients found
            </div>
          ) : (
            <div className="space-y-4">
              {type === "journey" && (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Checkbox
                      checked={
                        paginatedRecipients.some(
                          (r) => r.status.toLowerCase() === "failed"
                        ) &&
                        paginatedRecipients
                          .filter((r) => r.status.toLowerCase() === "failed")
                          .every((r) => selectedSendIds.includes(r.id))
                      }
                      onCheckedChange={toggleSelectAllFailedOnPage}
                      aria-label="Select all failed emails on this page"
                    />
                    <span>Select failed on this page</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedSendIds([])}
                      disabled={selectedSendIds.length === 0}
                    >
                      Clear selection
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() =>
                        retryJourneyEmailsMutation.mutate(selectedSendIds)
                      }
                      disabled={
                        selectedSendIds.length === 0 ||
                        retryJourneyEmailsMutation.isPending
                      }
                    >
                      {retryJourneyEmailsMutation.isPending ? (
                        <span className="h-4 w-4 mr-2 rounded-full border-2 border-b-transparent border-white animate-spin" />
                      ) : null}
                      Retry selected
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => clearFailedJourneyEmailsMutation.mutate()}
                      disabled={clearFailedJourneyEmailsMutation.isPending}
                    >
                      {clearFailedJourneyEmailsMutation.isPending ? (
                        <span className="h-4 w-4 mr-2 rounded-full border-2 border-b-transparent border-foreground animate-spin" />
                      ) : null}
                      Clear failed (last 24h)
                    </Button>
                    {selectedSendIds.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {selectedSendIds.length} selected
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {type === "journey" && (
                        <TableHead className="w-10"></TableHead>
                      )}
                      <TableHead>Email</TableHead>
                      <TableHead>Username</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Events</TableHead>
                      <TableHead>Sent At</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedRecipients.map((recipient) => (
                      <TableRow key={recipient.id}>
                        {type === "journey" && (
                          <TableCell className="w-10">
                            {recipient.status.toLowerCase() === "failed" ? (
                              <Checkbox
                                checked={selectedSendIds.includes(recipient.id)}
                                onCheckedChange={() =>
                                  toggleRecipientSelection(recipient.id)
                                }
                                aria-label={`Select send ${recipient.id}`}
                              />
                            ) : null}
                          </TableCell>
                        )}
                        <TableCell className="font-medium">
                          {recipient.email}
                        </TableCell>
                        <TableCell>{recipient.username || "—"}</TableCell>
                        <TableCell>
                          {getStatusBadge(recipient.status)}
                        </TableCell>
                        <TableCell>
                          {recipient.events && recipient.events.length > 0 ? (
                            <div className="flex gap-1 flex-wrap">
                              {recipient.events.map((event, idx) => (
                                <span key={idx} className="text-xs">
                                  {getEventTypeBadge(event.type)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {recipient.sentAt
                            ? formatDatePretty(recipient.sentAt)
                            : recipient.status.toLowerCase() === "failed"
                            ? "—"
                            : "Pending"}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {recipient.suppressionReason
                            ? `Suppressed: ${recipient.suppressionReason}`
                            : recipient.error || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Page {currentRecipientsPage} of {totalRecipientPages}
                </span>
                <div className="space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRecipientsPage((p) => Math.max(1, p - 1))}
                    disabled={currentRecipientsPage <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setRecipientsPage((p) =>
                        Math.min(totalRecipientPages, p + 1)
                      )
                    }
                    disabled={currentRecipientsPage >= totalRecipientPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading events...
            </div>
          ) : allEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No events tracked yet
            </div>
          ) : (
            <div className="space-y-4">
              <Tabs
                value={activeEventType}
                onValueChange={(v) => {
                  setActiveEventType(v as typeof activeEventType);
                  setEventsPage(1);
                }}
              >
                <TabsList className="flex flex-wrap gap-2 justify-start">
                  {(
                    [
                      "delivered",
                      "open",
                      "click",
                      "bounce",
                      "spam",
                      "unsubscribe",
                    ] as const
                  ).map((eventType) => {
                    // Map event types to EventStats keys (opened/clicked vs open/click)
                    const statsKey: keyof EventStats | null = (() => {
                      switch (eventType) {
                        case "delivered":
                          return "delivered";
                        case "open":
                          return "opened";
                        case "click":
                          return "clicked";
                        case "bounce":
                          return "bounced";
                        case "spam":
                          return "spam";
                        case "unsubscribe":
                          return "unsubscribe";
                        default:
                          return null;
                      }
                    })();

                    const count =
                      eventStats &&
                      statsKey &&
                      typeof eventStats[statsKey] === "number"
                        ? eventStats[statsKey]
                        : allEvents.filter(
                            (e) => e.type.toLowerCase() === eventType
                          ).length;
                    if (count === 0) return null;
                    return (
                      <TabsTrigger key={eventType} value={eventType}>
                        {getEventTypeBadge(eventType)}
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({count})
                        </span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </Tabs>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Time</TableHead>
                      {activeEventType === "click" && (
                        <TableHead>URL</TableHead>
                      )}
                      {activeEventType === "bounce" && (
                        <TableHead>Reason</TableHead>
                      )}
                      <TableHead>IP Address</TableHead>
                      <TableHead>User Agent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">
                          {event.recipientEmail}
                        </TableCell>
                        <TableCell>
                          {formatDatePretty(event.occurredAt)}
                        </TableCell>
                        {activeEventType === "click" && (
                          <TableCell>
                            {event.clickUrl ? (
                              <a
                                href={event.clickUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1"
                              >
                                {event.clickUrl.length > 50
                                  ? `${event.clickUrl.substring(0, 50)}...`
                                  : event.clickUrl}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        )}
                        {activeEventType === "bounce" && (
                          <TableCell className="max-w-xs truncate">
                            {event.bounceReason || "—"}
                          </TableCell>
                        )}
                        <TableCell>{event.ipAddress || "—"}</TableCell>
                        <TableCell className="max-w-xs truncate">
                          {event.userAgent || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Page {currentEventsPage} of {totalEventPages}
                </span>
                <div className="space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEventsPage((p) => Math.max(1, p - 1))}
                    disabled={currentEventsPage <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setEventsPage((p) => Math.min(totalEventPages, p + 1))
                    }
                    disabled={currentEventsPage >= totalEventPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="space-y-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading timeline...
            </div>
          ) : allEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No events tracked yet
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTimelineEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>
                          {formatDatePretty(event.occurredAt)}
                        </TableCell>
                        <TableCell>{getEventTypeBadge(event.type)}</TableCell>
                        <TableCell className="font-medium">
                          {event.recipientEmail}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            {event.clickUrl && (
                              <div>
                                <span className="text-muted-foreground">
                                  URL:{" "}
                                </span>
                                <a
                                  href={event.clickUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline"
                                >
                                  {event.clickUrl.length > 40
                                    ? `${event.clickUrl.substring(0, 40)}...`
                                    : event.clickUrl}
                                </a>
                              </div>
                            )}
                            {event.bounceReason && (
                              <div>
                                <span className="text-muted-foreground">
                                  Reason:{" "}
                                </span>
                                {event.bounceReason}
                              </div>
                            )}
                            {event.ipAddress && (
                              <div>
                                <span className="text-muted-foreground">
                                  IP:{" "}
                                </span>
                                {event.ipAddress}
                              </div>
                            )}
                            {event.location && (
                              <div>
                                <span className="text-muted-foreground">
                                  Location:{" "}
                                </span>
                                {event.location}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Page {currentTimelinePage} of {totalTimelinePages}
                </span>
                <div className="space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setTimelinePage((p) => Math.max(1, p - 1))}
                    disabled={currentTimelinePage <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setTimelinePage((p) =>
                        Math.min(totalTimelinePages, p + 1)
                      )
                    }
                    disabled={currentTimelinePage >= totalTimelinePages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
